import * as React from 'react'

type UsePageVisibilityRefreshOptions = {
  /**
   * Whether the refresh handler should be active
   * @default true
   */
  isEnabled?: boolean
}

/**
 * Hook to handle automatic refresh when page becomes visible after being hidden
 * @param callback Function to call when page becomes visible
 * @param shouldRefresh Function that determines if the callback should be executed
 * @param options Configuration options
 */
export function usePageVisibilityRefresh(
  callback: () => void,
  shouldRefresh: () => boolean,
  options: UsePageVisibilityRefreshOptions = {},
): void {
  const { isEnabled = true } = options

  // Track previous visibility state to detect transitions from hidden to visible
  const previousVisibilityRef = React.useRef<DocumentVisibilityState>(
    document.visibilityState,
  )

  React.useEffect(() => {
    if (!isEnabled) return

    function handleVisibilityChange(): void {
      const currentVisibility = document.visibilityState
      const previousVisibility = previousVisibilityRef.current

      // Only trigger callback when transitioning from hidden to visible
      // and when the condition is met
      if (
        previousVisibility === 'hidden' &&
        currentVisibility === 'visible' &&
        shouldRefresh()
      ) {
        callback()
      }

      // Update the previous visibility state
      previousVisibilityRef.current = currentVisibility
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return (): void =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [callback, shouldRefresh, isEnabled])
}
